/* eslint-disable complexity */
import {
    STAxPos,
    STBarDir,
    STBarGrouping,
    STBuiltInUnit,
    STCrossBetween,
    STCrosses,
    STDLblPos,
    STGrouping,
    STLayoutMode,
    STLayoutTarget,
    STLblAlgn,
    STLegendPos,
    STMarkerStyleDmlC,
    STOfPieType,
    STOrientationDmlC,
    STRadarStyle,
    STScatterStyle, STSizeRepresents,
    STSplitType,
    STTickLblPos,
    STTickMark,
    STTimeUnit,
    STTrendlineType,
    SubChartType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import isNil from 'lodash/isNil';
import { UIModelType } from '../constant';
import type { IChartModel as  IChartModelV2 } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v2/chart-model.interface';
import type { ISubChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';

type PickEnum<T, K extends T> = {
    [P in keyof K]: P extends K ? P : never;
};

// 图表子表类型，从UIModelType中获取子集
export type ChartType = PickEnum<UIModelType,
    | UIModelType.BAR
    | UIModelType.BAR_3D
    | UIModelType.LINE
    | UIModelType.LINE_3D
    | UIModelType.PIE
    | UIModelType.PIE_3D
    | UIModelType.RADAR
    // 圆环图
    | UIModelType.DOUGHNUT
    // 散点图
    | UIModelType.SCATTER
    // 面积图
    | UIModelType.AREA
    | UIModelType.AREA_3D
    // 气泡图
    | UIModelType.BUBBLE
    // 股价图
    | UIModelType.STOCK
    // 子母图
    | UIModelType.OF_PIE
>;

export const ChartType = {
    BAR: UIModelType.BAR as ChartType,
    BAR_3D: UIModelType.BAR_3D as ChartType,
    LINE: UIModelType.LINE as ChartType,
    LINE_3D: UIModelType.LINE_3D as ChartType,
    PIE: UIModelType.PIE as ChartType,
    PIE_3D: UIModelType.PIE_3D as ChartType,
    RADAR: UIModelType.RADAR as ChartType,
    // 圆环图
    DOUGHNUT: UIModelType.DOUGHNUT as ChartType,
    // 散点图
    SCATTER: UIModelType.SCATTER as ChartType,
    // 面积图
    AREA: UIModelType.AREA as ChartType,
    AREA_3D: UIModelType.AREA_3D as ChartType,
    // 气泡图
    BUBBLE: UIModelType.BUBBLE as ChartType,
    // 股价图
    STOCK: UIModelType.STOCK as ChartType,
    // 子母图
    OF_PIE: UIModelType.OF_PIE as ChartType,
};

export function getMapKey<T>(map: T, type: T[keyof T]): keyof T {
    const key = Object.keys(map as object).find(key => map[key] === type) as keyof T;
    return key;
}

// @ts-ignore
export const subChartTypeMap: Record<ChartType, SubChartType> = {
    [ChartType.BAR]: SubChartType.BarChart,
    [ChartType.BAR_3D]: SubChartType.Bar3DChart,
    [ChartType.LINE]: SubChartType.LineChart,
    [ChartType.LINE_3D]: SubChartType.Line3DChart,
    [ChartType.PIE]: SubChartType.PieChart,
    [ChartType.PIE_3D]: SubChartType.Pie3DChart,
    [ChartType.RADAR]: SubChartType.RadarChart,
    // 圆环图
    [ChartType.DOUGHNUT]: SubChartType.DoughnutChart,
    // 散点图
    [ChartType.SCATTER]: SubChartType.ScatterChart,
    // 面积图
    [ChartType.AREA]: SubChartType.AreaChart,
    [ChartType.AREA_3D]: SubChartType.Area3DChart,
    // 气泡图
    [ChartType.BUBBLE]: SubChartType.BubbleChart,
    // 股价图
    [ChartType.STOCK]: SubChartType.StockChart,
    // 子母图
    [ChartType.OF_PIE]: SubChartType.OfPieChart,
};
export function mapToSubChartType(chartType: ChartType): SubChartType {
    return subChartTypeMap[chartType] || SubChartType.Unknown;
}
export function mapToChartType(subChartType): ChartType {
    return getMapKey<typeof subChartTypeMap>(subChartTypeMap, subChartType);
}

function migrateFromMap(
    data: any,
    migrateMap: Record<string, [string, ((val: any) => any)?]>,
) {
    Object.keys(migrateMap).forEach((oldKey) => {
        const oldValue = data[oldKey];
        if (!isNil(oldValue)) {
            const [newKey, migrate] = migrateMap[oldKey];
            data[newKey] = migrate ? migrate(oldValue) : oldValue;
            if (newKey !== oldKey) {
                delete data.oldKey;
            }
        }
    });
    return data;
}

function migrateEnumStringToEnum<EnumType>(
    enumObject: EnumType,
    defaultVal: any,
): ((val: any) => EnumType) {
    return (enumKey: any) => enumObject[enumKey] || defaultVal;
}

export function migrateChart(chart: IChartModelV2) {
    const migrateMap:  Record<string, [string, ((val: any) => any)?]> = {
        title: ['title', migrateChartTitle],
        plotArea: ['plotArea', migrateChartPlotArea],
        legend: ['legend', migrateChartLegend],
    };
    return migrateFromMap(chart, migrateMap);
}

export function migrateSubChart(subChart: ISubChartModel) {
    migrateSubChartSharedProps(subChart);
    const { type } = subChart;
    switch (type) {
        case SubChartType.BarChart:
        case SubChartType.Bar3DChart:
            return migrateBarChart(subChart);
        case SubChartType.LineChart:
        case SubChartType.Line3DChart:
            return migrateLineChart(subChart);
        case SubChartType.AreaChart:
        case SubChartType.Area3DChart:
            return migrateAreaChart(subChart);
        case SubChartType.PieChart:
        case SubChartType.Pie3DChart:
            return migratePieChart(subChart);
        case SubChartType.DoughnutChart:
            return migrateDoughnutChart(subChart);
        case SubChartType.RadarChart:
            return migrateRadarChart(subChart);
        case SubChartType.ScatterChart:
            return migrateSatterChart(subChart);
        case SubChartType.StockChart:
            return migrateStockChart(subChart);
        case SubChartType.OfPieChart:
            return migrateOfpieChart(subChart);
        case SubChartType.BubbleChart:
            return migrateBubbleChart(subChart);
        default:
            return migrateBarChart(subChart);
    }
}

function migrateChartTitle(data) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        layout: ['layout', migrateChartLayout],
    };
    return migrateFromMap(data, migrateMap);
}

function migrateChartPlotArea(data) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        layout: ['layout', migrateChartLayout],
        // charts: ['charts', migratePlotCharts],
    };

    const { categoryAxes, dateAxes, valueAxes } = data;
    [categoryAxes, dateAxes, valueAxes].forEach((axes) => {
        migrateAxes(axes);
    });

    return migrateFromMap(data, migrateMap);
}

function migrateAxes(axes)  {
    return axes?.map((axis) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            autoLabeled: ['autoLabeled', migrateNumberToBoolean],
            scaling: ['scaling', migrateScaling],
            position: ['position', migrateEnumStringToEnum(STAxPos, STAxPos.PBDEFAULT)],
            majorTickMark: ['majorTickMark', migrateEnumStringToEnum(STTickMark, STTickMark.PBDEFAULT)],
            minorTickMark: ['minorTickMark', migrateEnumStringToEnum(STTickMark, STTickMark.PBDEFAULT)],
            tickLabelPosition: ['tickLabelPosition', migrateEnumStringToEnum(STTickLblPos, STTickLblPos.PBDEFAULT)],
            crosses: ['crosses', migrateEnumStringToEnum(STCrosses, STCrosses.PBDEFAULT)],
            crossBetween: ['crossBetween', migrateEnumStringToEnum(STCrossBetween, STCrossBetween.PBDEFAULT)],
            lblAlgn: ['lblAlgn', migrateEnumStringToEnum(STLblAlgn, STLblAlgn.PBDEFAULT)],
            units: ['units', migrateDispUnits],
            // 日期坐标轴独有
            baseTimeUnit: ['baseTimeUnit', migrateEnumStringToEnum(STTimeUnit, STTimeUnit.PBDEFAULT)],
            majorTimeUnit: ['majorTimeUnit', migrateEnumStringToEnum(STTimeUnit, STTimeUnit.PBDEFAULT)],
            minorTimeUnit: ['minorTimeUnit', migrateEnumStringToEnum(STTimeUnit, STTimeUnit.PBDEFAULT)],
        };

        migrateFromMap(axis, migrateMap);
        return axis;
    });
}

function migrateChartLegend(data) {
    const migrateMap:  Record<string, [string, ((val: any) => any)?]> = {
        layout: ['layout', migrateChartLayout],
        position: ['position', migrateEnumStringToEnum(STLegendPos, STLegendPos.PBDEFAULT)],
    };
    return migrateFromMap(data, migrateMap);
}

function migrateChartLayout(data) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        leftMode: ['leftMode', migrateEnumStringToEnum(STLayoutMode, STLayoutMode.PBDEFAULT)],
        topMode: ['topMode', migrateEnumStringToEnum(STLayoutMode, STLayoutMode.PBDEFAULT)],
        widthMode: ['widthMode', migrateEnumStringToEnum(STLayoutMode, STLayoutMode.PBDEFAULT)],
        heightMode: ['heightMode', migrateEnumStringToEnum(STLayoutMode, STLayoutMode.PBDEFAULT)],
        layoutTarget: ['layoutTarget', migrateEnumStringToEnum(STLayoutTarget, STLayoutTarget.PBDEFAULT)],
    };
    return migrateFromMap(data, migrateMap);
}

function migratePlotCharts(plotCharts) {
    return plotCharts?.map(migrateSubChart);
}


function migrateSubChartSharedProps(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        dataLabels: ['dataLabels', migrateDLbls],
        series: ['series', migrateSer],
        originType: ['originType', mapToSubChartType],
        // v2系列线解析不是数组，转成3的数组类型
        serLines: ['serLines', v => [v]],
    };
    return migrateFromMap(subChart, migrateMap);
}

function migrateSer(ser) {
    return ser.map(serData => migrateSerSharedProps(serData));
}

function migrateSerSharedProps(ser) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        dataList: ['dataList', migrateDataList],
        dataLabels: ['dataLabels', migrateDLbls],
        trendlines: ['trendlines', migrateTrendline],
        otherDPt: ['otherDPt', migrateDPt],
        otherDLbl: ['otherDLbl', migrateDLbl],
        marker: ['marker', migrateMarker],
    };
    return migrateFromMap(ser, migrateMap);
}

function migrateBarChart(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        direction: ['direction', migrateEnumStringToEnum(STBarDir, STBarDir.PBDEFAULT)],
        grouping: ['grouping', migrateEnumStringToEnum(STBarGrouping, STBarGrouping.PBDEFAULT)],
    };
    return migrateFromMap(subChart, migrateMap);
}

function migrateLineChart(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        grouping: ['grouping', migrateEnumStringToEnum(STGrouping, STGrouping.PBDEFAULT)],
    };
    return migrateFromMap(subChart, migrateMap);
}

function migrateAreaChart(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        grouping: ['grouping', migrateEnumStringToEnum(STGrouping, STGrouping.PBDEFAULT)],
    };
    return migrateFromMap(subChart, migrateMap);
}

function migratePieChart(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
    };
    return migrateFromMap(subChart, migrateMap);
}

function migrateDoughnutChart(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
    };
    return migrateFromMap(subChart, migrateMap);
}

function migrateRadarChart(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        radarStyle: ['radarStyle', migrateEnumStringToEnum(STRadarStyle, STRadarStyle.PBDEFAULT)],
    };
    return migrateFromMap(subChart, migrateMap);
}

function migrateSatterChart(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        scatterStyle: ['scatterStyle', migrateEnumStringToEnum(STScatterStyle, STScatterStyle.PBDEFAULT)],
    };
    return migrateFromMap(subChart, migrateMap);
}

function migrateStockChart(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
    };
    return migrateFromMap(subChart, migrateMap);
}

function migrateOfpieChart(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        ofPieType: ['ofPieType', migrateEnumStringToEnum(STOfPieType, STOfPieType.PBDEFAULT)],
        splitType: ['splitType', migrateEnumStringToEnum(STSplitType, STSplitType.PBDEFAULT)],
    };
    return migrateFromMap(subChart, migrateMap);
}

function migrateBubbleChart(subChart) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        sizeRepresents: ['sizeRepresents', migrateEnumStringToEnum(STSizeRepresents, STSizeRepresents.PBDEFAULT)],
    };
    return migrateFromMap(subChart, migrateMap);
}

function migrateDataList(dataList) {
    return dataList.map(data => migrateDataListItem(data));
}

function migrateDataListItem(dataListItem) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        dataPoint: ['dataPoint', migrateDPt],
        dataLabel: ['dataLabel', migrateDLbl],
    };
    return migrateFromMap(dataListItem, migrateMap);
}

function migrateDLbls(data) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        dataLabel: ['dataLabel', migrateDLbl],
    };
    migrateDLblSharedProps(data);
    return migrateFromMap(data, migrateMap);
}

function migrateDLbl(data) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        layout: ['layout', migrateChartLayout],
    };
    migrateDLblSharedProps(data);
    return migrateFromMap(data, migrateMap);
}

function migrateDLblSharedProps(data) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        scaling: ['scaling', migrateScaling],
        labelPosition: ['labelPosition', migrateEnumStringToEnum(STDLblPos, STDLblPos.PBDEFAULT)],
        showLegendKey: ['showLegendKey', migrateNumberToBoolean],
        showVal: ['showVal', migrateNumberToBoolean],
        showCategoryName: ['showCategoryName', migrateNumberToBoolean],
        showSeriesName: ['showSeriesName', migrateNumberToBoolean],
        showPercent: ['showPercent', migrateNumberToBoolean],
        showBubbleSize: ['showBubbleSize', migrateNumberToBoolean],
    };
    return migrateFromMap(data, migrateMap);
}

function migrateTrendline(trendlineList) {
    return trendlineList.map((trendline) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            type: ['type', migrateEnumStringToEnum(STTrendlineType, STTrendlineType.PBDEFAULT)],
            label: ['label', migrateTrendlineLbl],
        };
        return migrateFromMap(trendline, migrateMap);
    });
}

function migrateTrendlineLbl(data) {
    return migrateChartTitle(data);
}

function migrateDPt(data) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        marker: ['marker', migrateMarker],
    };
    return migrateFromMap(data, migrateMap);
}

function migrateMarker(data) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        symbol: ['symbol', migrateEnumStringToEnum(STMarkerStyleDmlC, STMarkerStyleDmlC.PBDEFAULT)],
    };
    return migrateFromMap(data, migrateMap);
}

function migrateNumberToBoolean(number: number | null) {
    return !!number;
}

function migrateScaling(scaling) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        orientation: ['orientation', migrateEnumStringToEnum(STOrientationDmlC, STOrientationDmlC.PBDEFAULT)],
    };
    return migrateFromMap(scaling, migrateMap);
}

function migrateDispUnits(dispUnits) {
    const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
        unit: ['unit', migrateEnumStringToEnum(STBuiltInUnit, STBuiltInUnit.PBDEFAULT)],
    };
    return migrateFromMap(dispUnits, migrateMap);
}
